import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Package, FlaskConical, Loader2, CheckCircle, Apple, Sparkles } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { useSuggestions } from '../hooks/useSuggestions';
import type { ProductType } from '../types';

interface ProductAddModalProps {
  isOpen: boolean;
  onClose: () => void;
  productType: ProductType;
}

type AddType = 'product' | 'ingredient';
type Step = 'type' | 'form' | 'confirm' | 'success';

const STEPS: Step[] = ['type', 'form', 'confirm', 'success'];

export function ProductAddModal({
  isOpen,
  onClose,
  productType: initialProductType,
}: ProductAddModalProps) {
  const { t } = useTranslation('scanner');
  const [step, setStep] = useState<Step>('type');
  const [addType, setAddType] = useState<AddType | null>(null);
  const [productType, setProductType] = useState<ProductType>(initialProductType);

  const { submitProductSuggestion, submitIngredientReport, isSubmitting, error, clearError } = useSuggestions();

  // Product fields
  const [brand, setBrand] = useState('');
  const [productName, setProductName] = useState('');
  const [similarFound, setSimilarFound] = useState<boolean | null>(null);
  const [productNotes, setProductNotes] = useState('');

  // Ingredient fields
  const [ingredientName, setIngredientName] = useState('');
  const [foundInProduct, setFoundInProduct] = useState('');
  const [ingredientNotes, setIngredientNotes] = useState('');

  const currentStepIndex = STEPS.indexOf(step);

  const handleTypeSelect = (type: AddType) => {
    setAddType(type);
    setStep('form');
  };

  const handleGoToConfirm = () => {
    setStep('confirm');
  };

  const handleSubmit = async () => {
    clearError();

    if (addType === 'product') {
      if (!brand.trim() || !productName.trim() || similarFound === null) {
        return;
      }

      const result = await submitProductSuggestion({
        brand: brand.trim(),
        product_name: productName.trim(),
        product_type: productType,
        similar_found: similarFound,
        notes: productNotes.trim() || undefined,
      });

      if (result) {
        setStep('success');
      }
    } else {
      if (!ingredientName.trim()) {
        return;
      }

      const result = await submitIngredientReport({
        ingredient_name: ingredientName.trim(),
        product_type: productType,
        found_in_product: foundInProduct.trim() || undefined,
        notes: ingredientNotes.trim() || undefined,
      });

      if (result) {
        setStep('success');
      }
    }
  };

  const handleClose = () => {
    setStep('type');
    setAddType(null);
    setProductType(initialProductType);
    setBrand('');
    setProductName('');
    setSimilarFound(null);
    setProductNotes('');
    setIngredientName('');
    setFoundInProduct('');
    setIngredientNotes('');
    clearError();
    onClose();
  };

  const isProductFormValid = brand.trim() && productName.trim() && similarFound !== null;
  const isIngredientFormValid = ingredientName.trim();
  const isFormValid = addType === 'product' ? isProductFormValid : isIngredientFormValid;

  if (!isOpen) return null;

  // Success view
  if (step === 'success') {
    const successContent = (
      <>
        <div className="bottom-sheet-backdrop" onClick={handleClose} />
        <div className="bottom-sheet-container">
          <div className="bottom-sheet-modal">
            <div className="bottom-sheet-header">
              <h2>{t('addModal.successTitle')}</h2>
              <button
                onClick={handleClose}
                className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="bottom-sheet-content text-center">
              <div className="w-16 h-16 bg-pp-teal/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-pp-teal" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">
                {t('addModal.successMessage')}
              </h3>
              <p className="text-white/70">
                {addType === 'product'
                  ? t('addModal.successDescriptionProduct')
                  : t('addModal.successDescriptionIngredient')}
              </p>
            </div>

            <div className="bottom-sheet-footer">
              <button
                onClick={handleClose}
                className="flex-1 py-2.5 rounded-xl bg-pp-orange text-white font-medium hover:bg-pp-orange/90"
              >
                {t('addModal.close')}
              </button>
            </div>
          </div>
        </div>
      </>
    );
    return createPortal(successContent, document.body);
  }

  const modalContent = (
    <>
      <div className="bottom-sheet-backdrop" onClick={handleClose} />
      <div className="bottom-sheet-container">
        <div className="bottom-sheet-modal">
          {/* Header */}
          <div className="bottom-sheet-header">
            <div className="flex-1">
              <h2 className="!text-lg !font-semibold">
                {step === 'type' && t('addModal.stepChoose')}
                {step === 'form' && (addType === 'product' ? t('addModal.stepProduct') : t('addModal.stepIngredient'))}
                {step === 'confirm' && t('addModal.stepReview')}
              </h2>
              {/* Progress Indicator */}
              <div className="flex gap-1 mt-3">
                {STEPS.slice(0, -1).map((_, index) => (
                  <div
                    key={index}
                    className={`flex-1 h-1 rounded-full ${
                      index < currentStepIndex ? 'bg-pp-orange' : 'bg-white/20'
                    }`}
                  />
                ))}
              </div>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30 ml-4"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="bottom-sheet-content">
            {/* Step 1: Type Selection */}
            {step === 'type' && (
              <div className="space-y-3">
                <p className="text-sm text-white/70 mb-4">
                  {t('addModal.helpText')}
                </p>

                <button
                  onClick={() => handleTypeSelect('product')}
                  className="w-full glass-effect hover:bg-white/10 rounded-[15px] p-4 text-left transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-pp-dark/25 rounded-xl flex items-center justify-center">
                      <Package className="w-6 h-6 text-pp-orange" />
                    </div>
                    <div>
                      <h3 className="font-medium text-white">{t('addModal.suggestProduct')}</h3>
                      <p className="text-sm text-white/70">
                        {t('addModal.suggestProductHint')}
                      </p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => handleTypeSelect('ingredient')}
                  className="w-full glass-effect hover:bg-white/10 rounded-[15px] p-4 text-left transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-pp-dark/25 rounded-xl flex items-center justify-center">
                      <FlaskConical className="w-6 h-6 text-pp-orange" />
                    </div>
                    <div>
                      <h3 className="font-medium text-white">{t('addModal.reportIngredient')}</h3>
                      <p className="text-sm text-white/70">
                        {t('addModal.reportIngredientHint')}
                      </p>
                    </div>
                  </div>
                </button>
              </div>
            )}

            {/* Step 2: Product Form */}
            {step === 'form' && addType === 'product' && (
              <div className="space-y-4">
                {/* Product Type Slider */}
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-2">
                    {t('addModal.productType')}
                  </label>
                  <div className="glass-effect rounded-[15px] p-1 relative">
                    <div
                      className={`absolute top-1 bottom-1 w-[calc(50%-0.25rem)] bg-pp-orange rounded-[15px] transition-transform duration-300 ease-out ${
                        productType === 'cosmetic' ? 'translate-x-[calc(100%+0.25rem)]' : 'translate-x-0'
                      }`}
                    />
                    <div className="relative flex">
                      <button
                        type="button"
                        onClick={() => setProductType('food')}
                        className="flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-[15px] transition-colors z-10"
                      >
                        <Apple className="w-4 h-4 text-white" />
                        <span className="font-medium text-white text-sm">{t('addModal.food')}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setProductType('cosmetic')}
                        className="flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-[15px] transition-colors z-10"
                      >
                        <Sparkles className="w-4 h-4 text-white" />
                        <span className="font-medium text-white text-sm">{t('addModal.cosmetic')}</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Brand */}
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">
                    {t('addModal.brandLabel')}
                  </label>
                  <input
                    type="text"
                    value={brand}
                    onChange={(e) => setBrand(e.target.value)}
                    placeholder={t('addModal.brandPlaceholder')}
                    className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange"
                  />
                </div>

                {/* Product Name */}
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">
                    {t('addModal.productNameLabel')}
                  </label>
                  <input
                    type="text"
                    value={productName}
                    onChange={(e) => setProductName(e.target.value)}
                    placeholder={t('addModal.productNamePlaceholder')}
                    className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange"
                  />
                </div>

                {/* Similar Found */}
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-2">
                    {t('addModal.similarFound')}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setSimilarFound(true)}
                      className={`px-4 py-2 rounded-xl border transition-colors font-medium ${
                        similarFound === true
                          ? 'border-pp-orange bg-pp-orange/20 text-white'
                          : 'border-white/20 bg-white/10 text-white/70 hover:bg-white/15'
                      }`}
                    >
                      {t('addModal.yes')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setSimilarFound(false)}
                      className={`px-4 py-2 rounded-xl border transition-colors font-medium ${
                        similarFound === false
                          ? 'border-pp-orange bg-pp-orange/20 text-white'
                          : 'border-white/20 bg-white/10 text-white/70 hover:bg-white/15'
                      }`}
                    >
                      {t('addModal.no')}
                    </button>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">
                    {t('addModal.additionalLabel')}
                  </label>
                  <textarea
                    value={productNotes}
                    onChange={(e) => setProductNotes(e.target.value)}
                    placeholder={t('addModal.additionalPlaceholder')}
                    rows={3}
                    className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 resize-none focus:outline-none focus:ring-2 focus:ring-pp-orange"
                  />
                </div>

                {error && (
                  <div className="bg-red-500/20 text-red-300 px-3 py-2 rounded-xl text-sm border border-red-500/30">
                    {error}
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Ingredient Form */}
            {step === 'form' && addType === 'ingredient' && (
              <div className="space-y-4">
                {/* Product Type Slider */}
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-2">
                    {t('addModal.ingredientTypeLabel')}
                  </label>
                  <div className="glass-effect rounded-[15px] p-1 relative">
                    <div
                      className={`absolute top-1 bottom-1 w-[calc(50%-0.25rem)] bg-pp-orange rounded-[15px] transition-transform duration-300 ease-out ${
                        productType === 'cosmetic' ? 'translate-x-[calc(100%+0.25rem)]' : 'translate-x-0'
                      }`}
                    />
                    <div className="relative flex">
                      <button
                        type="button"
                        onClick={() => setProductType('food')}
                        className="flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-[15px] transition-colors z-10"
                      >
                        <Apple className="w-4 h-4 text-white" />
                        <span className="font-medium text-white text-sm">{t('addModal.food')}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setProductType('cosmetic')}
                        className="flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-[15px] transition-colors z-10"
                      >
                        <Sparkles className="w-4 h-4 text-white" />
                        <span className="font-medium text-white text-sm">{t('addModal.cosmetic')}</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Ingredient Name */}
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">
                    {t('addModal.ingredientNameLabel')}
                  </label>
                  <input
                    type="text"
                    value={ingredientName}
                    onChange={(e) => setIngredientName(e.target.value)}
                    placeholder={t('addModal.ingredientNamePlaceholder')}
                    className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange"
                  />
                </div>

                {/* Found in Product */}
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">
                    {t('addModal.ingredientProductLabel')}
                  </label>
                  <input
                    type="text"
                    value={foundInProduct}
                    onChange={(e) => setFoundInProduct(e.target.value)}
                    placeholder={t('addModal.ingredientProductPlaceholder')}
                    className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange"
                  />
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">
                    {t('addModal.ingredientAdditionalLabel')}
                  </label>
                  <textarea
                    value={ingredientNotes}
                    onChange={(e) => setIngredientNotes(e.target.value)}
                    placeholder={t('addModal.ingredientAdditionalPlaceholder')}
                    rows={3}
                    className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 resize-none focus:outline-none focus:ring-2 focus:ring-pp-orange"
                  />
                </div>

                {error && (
                  <div className="bg-red-500/20 text-red-300 px-3 py-2 rounded-xl text-sm border border-red-500/30">
                    {error}
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Confirm */}
            {step === 'confirm' && (
              <div className="space-y-4">
                <p className="text-sm text-white/70 mb-4">
                  {t('addModal.reviewHint')}
                </p>

                <div className="glass-effect rounded-[15px] p-4 space-y-3">
                  <div>
                    <span className="text-xs text-white/50 uppercase tracking-wider">{t('addModal.reviewTypeLabel')}</span>
                    <p className="text-white font-medium">
                      {addType === 'product' ? t('addModal.reviewProduct') : t('addModal.reviewIngredient')}
                    </p>
                  </div>

                  <div>
                    <span className="text-xs text-white/50 uppercase tracking-wider">{t('addModal.reviewCategoryLabel')}</span>
                    <p className="text-white font-medium">
                      {productType === 'food' ? t('addModal.food') : t('addModal.cosmetic')}
                    </p>
                  </div>

                  {addType === 'product' && (
                    <>
                      <div>
                        <span className="text-xs text-white/50 uppercase tracking-wider">{t('addModal.reviewBrandLabel')}</span>
                        <p className="text-white font-medium">{brand}</p>
                      </div>
                      <div>
                        <span className="text-xs text-white/50 uppercase tracking-wider">{t('addModal.reviewProductNameLabel')}</span>
                        <p className="text-white font-medium">{productName}</p>
                      </div>
                      <div>
                        <span className="text-xs text-white/50 uppercase tracking-wider">{t('addModal.reviewSimilarFoundLabel')}</span>
                        <p className="text-white font-medium">{similarFound ? t('addModal.yes') : t('addModal.no')}</p>
                      </div>
                      {productNotes && (
                        <div>
                          <span className="text-xs text-white/50 uppercase tracking-wider">{t('addModal.reviewNotesLabel')}</span>
                          <p className="text-white/90 text-sm">{productNotes}</p>
                        </div>
                      )}
                    </>
                  )}

                  {addType === 'ingredient' && (
                    <>
                      <div>
                        <span className="text-xs text-white/50 uppercase tracking-wider">{t('addModal.reviewIngredient')}</span>
                        <p className="text-white font-medium">{ingredientName}</p>
                      </div>
                      {foundInProduct && (
                        <div>
                          <span className="text-xs text-white/50 uppercase tracking-wider">{t('addModal.reviewFoundInLabel')}</span>
                          <p className="text-white font-medium">{foundInProduct}</p>
                        </div>
                      )}
                      {ingredientNotes && (
                        <div>
                          <span className="text-xs text-white/50 uppercase tracking-wider">{t('addModal.reviewNotesLabel')}</span>
                          <p className="text-white/90 text-sm">{ingredientNotes}</p>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <p className="text-xs text-white/50 text-center">
                  {t('addModal.teamReview')}
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bottom-sheet-footer">
            {step === 'type' && (
              <button
                onClick={handleClose}
                className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30"
              >
                {t('addModal.cancel')}
              </button>
            )}

            {step === 'form' && (
              <>
                <button
                  onClick={() => setStep('type')}
                  className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30"
                >
                  {t('addModal.back')}
                </button>
                <button
                  onClick={handleGoToConfirm}
                  disabled={!isFormValid}
                  className="flex-1 py-2.5 rounded-xl bg-pp-orange text-white font-medium hover:bg-pp-orange/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('addModal.next')}
                </button>
              </>
            )}

            {step === 'confirm' && (
              <>
                <button
                  onClick={() => setStep('form')}
                  className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30"
                >
                  {t('addModal.back')}
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="flex-1 py-2.5 rounded-xl bg-pp-orange text-white font-medium flex items-center justify-center hover:bg-pp-orange/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    t('addModal.submit')
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
  return createPortal(modalContent, document.body);
}
